import { reactive } from "vue";
import { type BankAccount } from "@/models/bank-account.model";
import bankAccountsService from '@/services/bank-accounts.service';
import { type PlaidLinkOnSuccessMetadata } from '@/lib/plaid';

enum PlaidLinkAction {
  LinkAccount = 'link-account',
  VerifyMicroDeposit = 'verify-micro'
}

export type BankAccountsStoreState = {
  bankAccounts: BankAccount[];
  isLoadingBankAccounts: boolean;
  isPlaidLinkInProgress: boolean;
  plaidLinkAction: `${PlaidLinkAction}` | null;
  plaidLinkBankAccountId: null | string;
  plaidLinkToken: null | string;
  selectedBankAccount: BankAccount | null;
  showAddRoutingNumberModal: boolean;
}

export type BankAccountsStore = {
  closeAddRoutingNumberModal: typeof closeAddRoutingNumberModal,
  initializeAddRoutingNumberModal: typeof initializeAddRoutingNumberModal,
  initializePlaidLinkForAddBankAccount: typeof initializePlaidLinkForAddBankAccount,
  initializePlaidLinkForVerifyMicrodeposit: typeof initializePlaidLinkForVerifyMicrodeposit,
  loadBankAccounts: typeof loadBankAccounts,
  processPlaidLinkResponse: typeof processPlaidLinkResponse,
  resetPlaidLink: typeof resetPlaidLink,
  state: BankAccountsStoreState,
}

const state: BankAccountsStoreState = reactive({
  bankAccounts: [],
  isLoadingBankAccounts: false,
  isPlaidLinkInProgress: false,
  plaidLinkAction: null,
  plaidLinkBankAccountId: null,
  plaidLinkToken: null,
  selectedBankAccount: null,
  showAddRoutingNumberModal: false,
});

async function loadBankAccounts () {
  state.isLoadingBankAccounts = true;
  try {
    state.bankAccounts = await bankAccountsService.list();
  } finally {
    state.isLoadingBankAccounts = false;
  }
}

function resetPlaidLink ({
  isPlaidLinkInProgress = false,
  plaidLinkAction = null,
  plaidLinkBankAccountId = null,
  plaidLinkToken = null,
}: Partial<{
  isPlaidLinkInProgress: boolean
  plaidLinkAction: `${PlaidLinkAction}` | null;
  plaidLinkBankAccountId: null | string,
  plaidLinkToken: null | string,
}> = {
  isPlaidLinkInProgress: false,
  plaidLinkAction: null,
  plaidLinkBankAccountId: null,
  plaidLinkToken: null,
}) {
  state.plaidLinkAction = plaidLinkAction;
  state.plaidLinkToken = plaidLinkToken;
  state.plaidLinkBankAccountId = plaidLinkBankAccountId;
  state.isPlaidLinkInProgress = isPlaidLinkInProgress;
}

async function initializePlaidLinkForAddBankAccount () {
  try {
    resetPlaidLink({
      isPlaidLinkInProgress: true,
      plaidLinkAction: PlaidLinkAction.LinkAccount,
    });
    const { linkToken } = await bankAccountsService.createPlaidLinkToken();
    state.plaidLinkToken = linkToken;
  } catch (error) {
    resetPlaidLink();
    throw error;
  }
}

async function initializePlaidLinkForVerifyMicrodeposit (bankAccountId: string) {
  try {
    resetPlaidLink({
      isPlaidLinkInProgress: true,
      plaidLinkAction: PlaidLinkAction.VerifyMicroDeposit,
      plaidLinkBankAccountId: bankAccountId,
    });
    const { linkToken } = await bankAccountsService.createPlaidLinkToken({ bankAccountId });
    state.plaidLinkToken = linkToken;
  } catch (error) {
    resetPlaidLink();
    throw error;
  }
}

async function processPlaidLinkResponse (plaid_public_token: string, metadata: PlaidLinkOnSuccessMetadata) {
  try {
    switch (state.plaidLinkAction) {
      case PlaidLinkAction.LinkAccount:
        await bankAccountsService.create({
          bankAccountName: metadata.accounts[0].subtype,
          bankName: metadata.institution?.name,
          plaidAccountId: metadata.accounts[0].id,
          plaidLinkSessionId: metadata.link_session_id,
          plaidPublicToken: plaid_public_token,
          plaidVerificationStatus: metadata.accounts[0].verification_status,
        });
        break;
      case PlaidLinkAction.VerifyMicroDeposit:
        if (!state.plaidLinkBankAccountId) {
          throw new Error('Invalid bank account');
        }
        await bankAccountsService.updatePlaidLinkData(state.plaidLinkBankAccountId, {
          plaidAccountId: metadata.accounts[0].id,
          plaidLinkSessionId: metadata.link_session_id,
          plaidPublicToken: plaid_public_token,
          plaidVerificationStatus: metadata.accounts[0].verification_status,
        });
        break;
      default:
        throw new Error('Invalid plaid link action');
    }
  } finally {
    resetPlaidLink();
    await loadBankAccounts();
  }
}

function initializeAddRoutingNumberModal (bankAccountId: string) {
  const bankAccount = state.bankAccounts.find(({ id }) => id === bankAccountId);
  if (!bankAccount) {
    throw new Error('Unable to find the requested bank account');
  }
  state.selectedBankAccount = bankAccount;
  state.showAddRoutingNumberModal = true;
}

function closeAddRoutingNumberModal () {
  state.showAddRoutingNumberModal = false;
  state.selectedBankAccount = null;
}

export default {
  closeAddRoutingNumberModal,
  initializeAddRoutingNumberModal,
  initializePlaidLinkForAddBankAccount,
  initializePlaidLinkForVerifyMicrodeposit,
  loadBankAccounts,
  processPlaidLinkResponse,
  resetPlaidLink,
  state,
};
